import { useTenantContextHook } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useAssetsUserPermission } from '@atlassian/jira-router-resources-assets-user-permission/src/index.tsx';
import type { AssetsTenantContext } from './types.tsx';

/**
 * Hook that provides Assets-specific tenant context with role-based permissions.
 * When the feature gate is enabled, overrides admin permissions with Assets-specific roles.
 */
export const useAssetsTenantContext = (): AssetsTenantContext => {
	const [{ tenantContext }] = useTenantContextHook();
	const { isAssetsAdmin, isLicensedAssetsUser } = useAssetsUserPermission();

	// Override admin permissions with Assets-specific role when feature gate is enabled
	if (fg('assets-new-role-migrations')) {
		return {
			...tenantContext,
			isAdmin: !!isAssetsAdmin,
			isSiteAdmin: !!isAssetsAdmin,
			isLicensedAssetsUser: !!isLicensedAssetsUser,
		};
	}

	return tenantContext;
};

/**
 * Hook that returns whether the current user is an Assets administrator.
 */
export const useAssetsIsAdmin = (): boolean => {
	const tenantContext = useAssetsTenantContext();
	return tenantContext?.isAdmin ?? false;
};

/**
 * Hook that returns whether the current user is an Assets site administrator.
 */
export const useAssetsIsSiteAdmin = (): boolean => {
	const tenantContext = useAssetsTenantContext();
	return tenantContext?.isSiteAdmin ?? false;
};
